import { useCallback, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');
const API_URL = import.meta.env.VITE_API_URL || 'https://qboverifier.bio-mail.com/api/stripe';

// Helper: format USD with 2 decimal places
function formatUSDDecimal(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatUSD(amount: number): string {
  return `$${amount.toLocaleString('en-US')}`;
}

interface StripeEmbeddedCheckoutProps {
  planId: string;
  planName: string;
  term: string;
  planAmount: number;
  invoices: string;
  billingLabel: string;
  isYearly: boolean;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  onBack: () => void;
}

export default function StripeEmbeddedCheckout({
  planId,
  planName,
  term,
  planAmount,
  invoices,
  billingLabel,
  isYearly,
  taxRate,
  taxAmount,
  totalAmount,
  onBack,
}: StripeEmbeddedCheckoutProps) {
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const showTax = taxRate > 0 && taxAmount > 0;

  // Pre-flight check — verify the server responds before mounting EmbeddedCheckoutProvider
  useEffect(() => {
    let cancelled = false;

    const preflight = async () => {
      try {
        const probe = await fetch(`${API_URL}/create-embedded-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planId, planName, term }),
        });

        if (!probe.ok) {
          const data = await probe.json().catch(() => ({}));
          throw new Error(
            data.error ||
              `Server error (${probe.status}). Make sure the Node server is running and restarted.`
          );
        }

        const data = await probe.json();
        if (!data.clientSecret) {
          throw new Error(
            'No client secret returned. Restart the Node server so the /create-embedded-session endpoint is active.'
          );
        }

        // All good — let EmbeddedCheckoutProvider fetch it again via its own callback
        if (!cancelled) setReady(true);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not connect to payment server.');
      }
    };

    preflight();
    return () => { cancelled = true; };
  }, [planId, planName, term]);

  const fetchClientSecret = useCallback(async (): Promise<string> => {
    const response = await fetch(`${API_URL}/create-embedded-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planId, planName, term }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const msg = data.error || `Server error (${response.status})`;
      setError(msg);
      throw new Error(msg);
    }

    const data = await response.json();
    if (!data.clientSecret) {
      const msg = 'No client secret returned from server.';
      setError(msg);
      throw new Error(msg);
    }

    return data.clientSecret;
  }, [planId, planName, term]);

  return (
    <div
      className="min-h-screen"
      style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e8f4f8 100%)' }}
    >
      {/* ── Branded header ─────────────────────────────────────── */}
      <div className="pt-10 pb-6 text-center">
        <img
          src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/e002dead815e5e3725a361aa0576a674.png"
          alt="bioMail QBOVerifier"
          className="h-16 w-auto mx-auto mb-5 object-contain"
        />
        <div
          className="inline-block px-8 py-2.5 rounded-full text-white font-bold text-lg tracking-wide"
          style={{ backgroundColor: '#29B3E6' }}
        >
          bioMail QBOVerifier
        </div>
        <p className="text-gray-500 mt-3 text-sm flex items-center justify-center gap-1.5">
          <i className="ri-lock-line text-green-500"></i>
          Secure Checkout — Powered by Stripe
        </p>
      </div>

      {/* ── Order Summary ──────────────────────────────────────── */}
      <div className="px-6 pb-4">
        <div className="max-w-xl mx-auto bg-white rounded-2xl border border-gray-200 p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>
          <div className="space-y-0">

            {/* Plan row */}
            <div className="flex justify-between items-start pb-3 border-b border-gray-100">
              <div>
                <p className="font-semibold text-gray-900">{planName} Plan</p>
                <p className="text-sm" style={{ color: '#29B3E6' }}>{invoices}</p>
              </div>
              <p className="text-lg font-bold text-gray-900">{formatUSD(planAmount)}</p>
            </div>

            {/* Tax row — only shown when taxRate > 0 */}
            {showTax && (
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <p className="text-gray-700 font-medium">Tax ({taxRate}%)</p>
                <p className="text-gray-900 font-semibold">{formatUSDDecimal(taxAmount)}</p>
              </div>
            )}

            {/* Billing cycle row */}
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <p className="text-gray-500">Billing Cycle</p>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-700">{billingLabel}</p>
                {isYearly && (
                  <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">
                    Save 15%
                  </span>
                )}
              </div>
            </div>

            {/* Total row */}
            <div className="flex justify-between items-center pt-3">
              <p className="text-lg font-bold text-gray-900">Total Due Today</p>
              <p className="text-2xl font-bold" style={{ color: '#29B3E6' }}>
                {showTax ? formatUSDDecimal(totalAmount) : formatUSD(planAmount)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Checkout card ──────────────────────────────────────── */}
      <div className="px-6 pb-12">
        <div
          className="max-w-xl mx-auto bg-white rounded-2xl border border-gray-200 overflow-hidden"
          style={{ boxShadow: '0 4px 28px rgba(0,0,0,0.09)' }}
        >
          {/* Loading state — waiting for preflight */}
          {!ready && !error && (
            <div className="p-10 text-center">
              <div
                className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 mb-4"
                style={{ borderColor: '#29B3E6' }}
              ></div>
              <p className="text-gray-500 text-sm">Loading secure checkout…</p>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="p-10 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <p className="text-red-600 font-semibold mb-2">Could not load checkout</p>
              <p className="text-gray-500 text-sm mb-6">{error}</p>
              <button
                type="button"
                onClick={onBack}
                className="text-sm font-semibold text-white px-6 py-2.5 rounded-lg cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: '#29B3E6' }}
              >
                Try a different payment method
              </button>
            </div>
          )}

          {/* Embedded Stripe checkout — only mount after preflight succeeds */}
          {ready && !error && (
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ fetchClientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>

        <div className="text-center mt-6">
          <button
            type="button"
            onClick={onBack}
            className="text-gray-500 hover:text-gray-800 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-1.5"></i>
            Choose a different payment method
          </button>
        </div>
      </div>
    </div>
  );
}
